import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  UserCheck, UserX, Upload, FolderPlus, DollarSign, 
  FileText, Trash2, Download, Plus, X 
} from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI } from '../utils/authUtils';

const TrainingPortalAdmin = () => {
  // State for pending registrations
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approvedUsers, setApprovedUsers] = useState([]);
  
  // State for categories
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [newCategoryIcon, setNewCategoryIcon] = useState('📚');
  
  // State for materials
  const [materials, setMaterials] = useState([]);
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  
  // State for commissions
  const [commissions, setCommissions] = useState([]);
  const [newCommissionUserId, setNewCommissionUserId] = useState('');
  const [newCommissionAmount, setNewCommissionAmount] = useState('');
  const [newCommissionSaleAmount, setNewCommissionSaleAmount] = useState('');
  
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // For now, we'll use direct fetch calls since adminAPI functions exist
      const [categoriesData, usersData, commissionsData] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/training-categories`).then(res => res.json()),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/sales-rep-users`).then(res => res.json()),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/commissions/all`).then(res => res.json()),
      ]);

      setCategories(categoriesData);
      
      // Separate pending and approved users
      const pending = usersData.filter(u => !u.approved);
      const approved = usersData.filter(u => u.approved);
      setPendingUsers(pending);
      setApprovedUsers(approved);
      
      setCommissions(commissionsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (applicationId, approve) => {
    try {
      await adminAPI.approveSalesRep(applicationId, approve);
      toast.success(approve ? 'User Approved!' : 'User Rejected');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to process approval', {
        description: error.message
      });
    }
  };

  const handleToggleUserActive = async (userId) => {
    try {
      await adminAPI.toggleSalesRepActive(userId);
      toast.success('User status updated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update user', {
        description: error.message
      });
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.createCategory({
        name: newCategoryName,
        description: newCategoryDescription,
        icon: newCategoryIcon,
        order: categories.length + 1
      });
      
      toast.success('Category created successfully!');
      setNewCategoryName('');
      setNewCategoryDescription('');
      setNewCategoryIcon('📚');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to create category', {
        description: error.message
      });
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure? This will also delete all materials in this category.')) {
      return;
    }
    
    try {
      await adminAPI.deleteCategory(categoryId);
      toast.success('Category deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete category', {
        description: error.message
      });
    }
  };

  const handleUploadMaterial = async (e) => {
    e.preventDefault();
    
    if (!uploadFile) {
      toast.error('Please select a file to upload');
      return;
    }

    try {
      setLoading(true);
      await adminAPI.uploadMaterial(uploadCategory, uploadTitle, uploadDescription, uploadFile);
      
      toast.success('Material uploaded successfully!');
      setUploadTitle('');
      setUploadDescription('');
      setUploadFile(null);
      setUploadCategory('');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to upload material', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCommission = async (e) => {
    e.preventDefault();
    
    try {
      await adminAPI.createCommission({
        user_id: newCommissionUserId,
        sale_amount: parseFloat(newCommissionSaleAmount),
        commission_amount: parseFloat(newCommissionAmount),
        status: 'pending'
      });
      
      toast.success('Commission created successfully!');
      setNewCommissionUserId('');
      setNewCommissionAmount('');
      setNewCommissionSaleAmount('');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to create commission', {
        description: error.message
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-2 border-purple-200">
        <CardHeader>
          <CardTitle className="text-2xl">Training Portal Administration</CardTitle>
          <CardDescription>Manage sales reps, training materials, and commissions</CardDescription>
        </CardHeader>
      </Card>

      <Tabs defaultValue="registrations" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="registrations">
            Registrations ({pendingUsers.length})
          </TabsTrigger>
          <TabsTrigger value="categories">
            Categories ({categories.length})
          </TabsTrigger>
          <TabsTrigger value="materials">
            Materials
          </TabsTrigger>
          <TabsTrigger value="commissions">
            Commissions ({commissions.length})
          </TabsTrigger>
        </TabsList>

        {/* Pending Registrations Tab */}
        <TabsContent value="registrations">
          <Card className="border-2 border-gray-200">
            <CardHeader>
              <CardTitle>Pending Sales Rep Registrations</CardTitle>
              <CardDescription>Review and approve new sales representative applications</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No pending registrations
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingUsers.map((user) => (
                    <Card key={user.id} className="border border-purple-200">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <h3 className="font-bold text-lg">{user.full_name}</h3>
                            <p className="text-sm text-gray-600">{user.email}</p>
                            <p className="text-xs text-gray-500 mt-1">Registered: {new Date(user.created_at).toLocaleDateString()}</p>
                          </div>
                          <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">
                            Pending Approval
                          </Badge>
                        </div>
                        
                        <div className="flex space-x-2">
                          <Button 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleApproveUser(user.id, true)}
                          >
                            <UserCheck className="w-4 h-4 mr-2" />
                            Approve
                          </Button>
                          <Button 
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleApproveUser(user.id, false)}
                          >
                            <UserX className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Approved Users Section */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">Approved Sales Reps ({approvedUsers.length})</h3>
                <div className="space-y-2">
                  {approvedUsers.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{user.full_name}</p>
                        <p className="text-sm text-gray-600">{user.email}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant={user.active ? "success" : "secondary"}>
                          {user.active ? 'Active' : 'Inactive'}
                        </Badge>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleToggleUserActive(user.id)}
                        >
                          {user.active ? 'Deactivate' : 'Activate'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Create Category Card */}
            <Card className="border-2 border-blue-200">
              <CardHeader>
                <CardTitle>Create New Category</CardTitle>
                <CardDescription>Add a new training material category</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateCategory} className="space-y-4">
                  <div>
                    <Label htmlFor="categoryName">Category Name</Label>
                    <Input
                      id="categoryName"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="e.g., Generators 101"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="categoryDescription">Description</Label>
                    <Textarea
                      id="categoryDescription"
                      value={newCategoryDescription}
                      onChange={(e) => setNewCategoryDescription(e.target.value)}
                      placeholder="Brief description of this category"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="categoryIcon">Icon (Emoji)</Label>
                    <Input
                      id="categoryIcon"
                      value={newCategoryIcon}
                      onChange={(e) => setNewCategoryIcon(e.target.value)}
                      placeholder="📚"
                      maxLength={2}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                    <FolderPlus className="w-4 h-4 mr-2" />
                    Create Category
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Existing Categories Card */}
            <Card className="border-2 border-gray-200">
              <CardHeader>
                <CardTitle>Existing Categories</CardTitle>
                <CardDescription>Manage training material categories</CardDescription>
              </CardHeader>
              <CardContent>
                {categories.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No categories yet</p>
                ) : (
                  <div className="space-y-2">
                    {categories.map((category) => (
                      <div key={category.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl">{category.icon}</span>
                          <div>
                            <p className="font-medium">{category.name}</p>
                            <p className="text-sm text-gray-600">{category.description}</p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteCategory(category.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Materials Tab */}
        <TabsContent value="materials">
          <Card className="border-2 border-gray-200">
            <CardHeader>
              <CardTitle>Upload Training Material</CardTitle>
              <CardDescription>Add documents, PDFs, presentations, or videos</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUploadMaterial} className="space-y-4">
                <div>
                  <Label htmlFor="materialCategory">Category</Label>
                  <select
                    id="materialCategory"
                    value={uploadCategory}
                    onChange={(e) => setUploadCategory(e.target.value)}
                    className="w-full border rounded-md p-2"
                    required
                  >
                    <option value="">Select a category...</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.icon} {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="materialTitle">Title</Label>
                  <Input
                    id="materialTitle"
                    value={uploadTitle}
                    onChange={(e) => setUploadTitle(e.target.value)}
                    placeholder="e.g., Generator Installation Guide"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="materialDescription">Description</Label>
                  <Textarea
                    id="materialDescription"
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                    placeholder="Brief description of this material"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="materialFile">File</Label>
                  <Input
                    id="materialFile"
                    type="file"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Supported formats: PDF, Word, PowerPoint, Excel (Max 70MB)
                  </p>
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {loading ? 'Uploading...' : 'Upload Material'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Commissions Tab */}
        <TabsContent value="commissions">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Create Commission Card */}
            <Card className="border-2 border-green-200">
              <CardHeader>
                <CardTitle>Record New Commission</CardTitle>
                <CardDescription>Add commission for a sales rep</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateCommission} className="space-y-4">
                  <div>
                    <Label htmlFor="commissionUser">Sales Rep</Label>
                    <select
                      id="commissionUser"
                      value={newCommissionUserId}
                      onChange={(e) => setNewCommissionUserId(e.target.value)}
                      className="w-full border rounded-md p-2"
                      required
                    >
                      <option value="">Select a sales rep...</option>
                      {approvedUsers.filter(u => u.active).map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.full_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="saleAmount">Sale Amount (USD)</Label>
                    <Input
                      id="saleAmount"
                      type="number"
                      step="0.01"
                      value={newCommissionSaleAmount}
                      onChange={(e) => setNewCommissionSaleAmount(e.target.value)}
                      placeholder="5000.00"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="commissionAmount">Commission Amount (USD)</Label>
                    <Input
                      id="commissionAmount"
                      type="number"
                      step="0.01"
                      value={newCommissionAmount}
                      onChange={(e) => setNewCommissionAmount(e.target.value)}
                      placeholder="500.00"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full bg-green-600 hover:bg-green-700">
                    <DollarSign className="w-4 h-4 mr-2" />
                    Record Commission
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Commissions List Card */}
            <Card className="border-2 border-gray-200">
              <CardHeader>
                <CardTitle>Recent Commissions</CardTitle>
                <CardDescription>Commission tracking and history</CardDescription>
              </CardHeader>
              <CardContent>
                {commissions.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No commissions recorded yet</p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {commissions.slice(0, 10).map((commission) => {
                      const user = approvedUsers.find(u => u.id === commission.user_id);
                      return (
                        <div key={commission.id} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <p className="font-medium">{user?.full_name || 'Unknown'}</p>
                            <Badge 
                              variant={commission.status === 'paid' ? 'success' : 'secondary'}
                              className={commission.status === 'paid' ? 'bg-green-100 text-green-700' : ''}
                            >
                              {commission.status}
                            </Badge>
                          </div>
                          <div className="text-sm text-gray-600">
                            <p>Sale: ${commission.sale_amount?.toFixed(2) || '0.00'}</p>
                            <p>Commission: ${commission.commission_amount?.toFixed(2) || commission.amount?.toFixed(2) || '0.00'}</p>
                            <p className="text-xs">
                              {commission.sale_date ? new Date(commission.sale_date).toLocaleDateString() : 
                               commission.created_at ? new Date(commission.created_at).toLocaleDateString() : 'N/A'}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TrainingPortalAdmin;
